import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';

const CustomerSidebar = ({ onNavigate }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // Fetch system branding for company name and logo (accessible to all authenticated users)
  const { data: systemBranding, isLoading: brandingLoading, error: brandingError } = useQuery(
    'systemBranding',
    () => adminAPI.getSystemBranding(),
    {
      staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes (reduced from 5)
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (reduced from 10)
      retry: 2, // Retry twice on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      enabled: true // Always try to fetch - this endpoint is accessible to all authenticated users
    }
  );
  
  // Get company name and logo from branding or use fallback
  const companyName = systemBranding?.company_name || '3PL WMS Portal';
  const logoUrl = systemBranding?.has_logo_file 
    ? `/api/system-settings/logo?t=${systemBranding.logo_updated_at ? new Date(systemBranding.logo_updated_at).getTime() : Date.now()}` 
    : systemBranding?.logo_url;
  
  const handleLogout = async () => {
    await logout();
  };
  
  // Function to check if a link is active
  const isActive = (path) => {
    if (path === '/customer' && location.pathname === '/customer') {
      return true;
    }
    
    // Handle paths with query parameters for the new workflow structure
    if (path.includes('?')) {
      const [basePath, queryString] = path.split('?');
      if (location.pathname === basePath) {
        const pathParams = new URLSearchParams(queryString);
        const currentParams = new URLSearchParams(location.search);
        
        // Check if the type parameter matches
        const pathType = pathParams.get('type');
        const currentType = currentParams.get('type');
        
        if (pathType && currentType) {
          // Handle comma-separated types (e.g., "pick_pack,kit_build")
          const pathTypes = pathType.split(',').sort();
          const currentTypes = currentType.split(',').sort();
          return JSON.stringify(pathTypes) === JSON.stringify(currentTypes);
        }
        
        return pathType === currentType;
      }
      return false;
    }
    
    return location.pathname.startsWith(path) && path !== '/customer';
  };

  // Render the company branding header
  const renderCompanyBranding = () => {
    return (
      <div className="p-4 border-b border-blue-800/80 relative">
        {/* Mobile close button */}
        {onNavigate && (
          <button
            type="button"
            className="absolute top-3 right-3 md:hidden text-blue-200/80 hover:text-white focus:outline-none"
            onClick={onNavigate}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <div className="flex flex-col items-center space-y-2">
          {/* Logo */}
          {logoUrl ? (
            <div className="w-full flex justify-center items-center">
              <img
                src={logoUrl}
                alt={`${companyName} Logo`}
                className="max-w-[200px] max-h-[60px] object-contain mx-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ) : null}
          
          {/* Company Name */}
          <div 
            className="text-center text-white font-bold text-lg leading-tight"
            title={companyName}
          >
            <div className="truncate max-w-full">
              {companyName}
            </div>
          </div>
          
          {/* Customer Name */}
          <div className="text-center text-blue-100 text-sm">
            {user?.name || 'Customer Portal'}
          </div>
          
          {/* Customer Company Name */}
          {user?.company?.name && (
            <div className="text-center text-blue-200/80 text-xs">
              {user.company.name}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <aside className="w-64 bg-brand-navy text-white h-full flex flex-col">
      {renderCompanyBranding()}
      <nav className="mt-5 px-2 flex-1 overflow-y-auto" onClick={(e) => {
        // Close sidebar on mobile when a nav link is clicked
        if (onNavigate && e.target.closest('a')) onNavigate();
      }}>
        {/* Dashboard - Standalone */}
        <Link 
          to="/customer" 
          className={`${
            isActive('/customer') 
              ? 'bg-blue-950/60 text-white' 
              : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
          } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-6`}
        >
          <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Dashboard
        </Link>

        {/* Warehouse Operations Section */}
        <div className="mb-6">
          <h3 className="px-2 text-xs font-semibold text-blue-200/70 uppercase tracking-wider mb-3">
            Warehouse Operations
          </h3>
          
          {/* Inventory */}
          <Link 
            to="/customer/inventory" 
            className={`${
              isActive('/customer/inventory') 
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Inventory
          </Link>
          
          {/* Inbound */}
          <Link 
            to="/customer/requests?type=incoming,crossdock,inbound_manifest" 
            className={`${
              isActive('/customer/requests?type=incoming') || isActive('/customer/requests?type=incoming,crossdock') || isActive('/customer/requests?type=incoming,crossdock,inbound_manifest')
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707-.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            Inbound
          </Link>
          
          {/* Outbound */}
          <Link 
            to="/customer/requests?type=ship" 
            className={`${
              isActive('/customer/requests?type=ship') 
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Outbound
          </Link>
          
          {/* All Requests */}
          <Link 
            to="/customer/requests" 
            className={`${
              isActive('/customer/requests') && !location.search
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            All Requests
          </Link>
        </div>

        {/* Billing Section */}
        <div className="mb-6">
          <h3 className="px-2 text-xs font-semibold text-blue-200/70 uppercase tracking-wider mb-3">
            Billing
          </h3>

          {/* Storage Fees (live current-month projection) */}
          <Link
            to="/customer/storage-fees"
            className={`${
              isActive('/customer/storage-fees')
                ? 'bg-blue-950/60 text-white'
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Storage Fees
          </Link>

          {/* Invoices */}
          <Link
            to="/customer/invoices"
            className={`${
              isActive('/customer/invoices')
                ? 'bg-blue-950/60 text-white'
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Invoices
          </Link>

        </div>

        {/* Account Section */}
        <div className="mb-6">
          <h3 className="px-2 text-xs font-semibold text-blue-200/70 uppercase tracking-wider mb-3">
            Account
          </h3>
          
          {/* Company */}
          <Link 
            to="/customer/company" 
            className={`${
              isActive('/customer/company') || isActive('/customer/profile')
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M5 7V5a2 2 0 012-2h3v4m4-4h3a2 2 0 012 2v2m-2 0v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7m5 4h4m-4 4h4" />
            </svg>
            Company
          </Link>
          
          {/* User Management */}
          <Link 
            to="/customer/users" 
            className={`${
              isActive('/customer/users') 
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            User Management
          </Link>
          
          {/* Addresses */}
          <Link
            to="/customer/addresses"
            className={`${
              isActive('/customer/addresses') 
                ? 'bg-blue-950/60 text-white' 
                : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Addresses
          </Link>
          

        </div>
      </nav>
      <div className="border-t border-blue-800/80 px-2 py-3">
        <Link
          to="/customer/help"
          className={`${
            isActive('/customer/help')
              ? 'bg-blue-950/60 text-white'
              : 'text-blue-100 hover:bg-blue-900/70 hover:text-white'
          } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
          onClick={onNavigate}
        >
          <svg className="mr-3 h-5 w-5 text-blue-200/80 group-hover:text-blue-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9.228a4 4 0 115.657 5.657c-.75.75-1.885 1.145-1.885 2.115M12 19h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
          </svg>
          Help
        </Link>
      </div>
    </aside>
  );
};

export default CustomerSidebar; 
